import React, { useEffect, useState, useMemo } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { BackBar } from "../../components/BackBar";
import {
  CheckCircle2, Clock, Loader2, AlertCircle, XCircle, Banknote,
  Calendar as CalIcon, ShieldCheck,
} from "lucide-react";

const STATUS_META = {
  scheduled: { color: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock, fr: "Programmé", en: "Scheduled" },
  processing: { color: "bg-blue-50 text-blue-700 border-blue-300", icon: Loader2, fr: "En cours d'encaissement", en: "Processing" },
  paid: { color: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle2, fr: "Payé", en: "Paid" },
  manual_paid: { color: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle2, fr: "Payé (autre)", en: "Paid (other)" },
  failed: { color: "bg-amber-50 text-amber-800 border-amber-300", icon: AlertCircle, fr: "Échec — nouvelle tentative prévue", en: "Failed — retry scheduled" },
  abandoned: { color: "bg-red-50 text-red-700 border-red-300", icon: XCircle, fr: "À régulariser", en: "Action required" },
};

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US",
                  { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function ParentPaymentsHistory() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/parent/installments/mine")
       .then(r => setItems(r.data.items || []))
       .catch(() => {})
       .finally(() => setLoading(false));
  }, []);

  // Group by enrollment so the IBAN + student appears once per group
  const grouped = useMemo(() => {
    const m = new Map();
    items.forEach(i => {
      const k = i.enrollment_id;
      if (!m.has(k)) m.set(k, {
        student_name: i.student_name,
        sepa_last4: i.sepa_last4,
        sepa_bank_code: i.sepa_bank_code,
        items: [],
      });
      m.get(k).items.push(i);
    });
    return Array.from(m.values());
  }, [items]);

  const totals = useMemo(() => ({
    paid: items.filter(i => i.status === "paid" || i.status === "manual_paid")
               .reduce((s, i) => s + (i.amount || 0), 0),
    upcoming: items.filter(i => i.status === "scheduled" || i.status === "processing")
                   .reduce((s, i) => s + (i.amount || 0), 0),
    failed: items.filter(i => i.status === "failed" || i.status === "abandoned")
                 .reduce((s, i) => s + (i.amount || 0), 0),
  }), [items]);

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-10" data-testid="parent-payments-history-page">
      <BackBar to="/parent" fallbackLabelFr="Tableau de bord" fallbackLabelEn="Dashboard" />
      <p className="overline mb-2">{lang === "fr" ? "Mon espace" : "My space"}</p>
      <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">
        {lang === "fr" ? "Historique de paiements" : "Payment history"}
      </h1>
      <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
        {lang === "fr"
          ? "Retrouvez ici toutes vos échéances passées et à venir, leur statut bancaire et le mandat SEPA utilisé."
          : "Find all your past and upcoming installments, their bank status and the SEPA mandate used."}
      </p>

      {/* Quick totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        <SummaryCard label={lang === "fr" ? "Déjà encaissé" : "Already collected"} value={`${totals.paid.toFixed(2)} €`} tone="success" />
        <SummaryCard label={lang === "fr" ? "À venir" : "Upcoming"} value={`${totals.upcoming.toFixed(2)} €`} />
        <SummaryCard label={lang === "fr" ? "À régulariser" : "Needs action"} value={`${totals.failed.toFixed(2)} €`} tone={totals.failed > 0 ? "danger" : "muted"} />
      </div>

      {loading && (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
          {lang === "fr" ? "Chargement de l'historique…" : "Loading history…"}
        </CardContent></Card>
      )}

      {!loading && grouped.length === 0 && (
        <Card><CardContent className="p-8 text-sm text-muted-foreground text-center" data-testid="history-empty">
          {lang === "fr"
            ? "Aucune échéance n'est encore programmée. Elle apparaîtront ici dès la mise en place de votre mandat SEPA."
            : "No installments scheduled yet. They will appear here once your SEPA mandate is set up."}
        </CardContent></Card>
      )}

      {!loading && grouped.map((g, gi) => (
        <Card key={gi} className="mb-5" data-testid={`history-group-${gi}`}>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <h3 className="font-semibold text-lg">{g.student_name || (lang === "fr" ? "Élève" : "Student")}</h3>
              {g.sepa_last4 && (
                <Badge variant="outline" className="gap-1 text-[10px]">
                  <ShieldCheck className="w-3 h-3" />
                  IBAN •••• {g.sepa_last4}
                  {g.sepa_bank_code && <span className="text-muted-foreground"> · {g.sepa_bank_code}</span>}
                </Badge>
              )}
            </div>
            <div className="space-y-2.5">
              {g.items.map(i => {
                const meta = STATUS_META[i.status] || STATUS_META.scheduled;
                const Icon = meta.icon;
                return (
                  <div key={i.id} className="flex items-center gap-3 rounded-lg border bg-card p-3"
                       data-testid={`history-item-${i.id}`}>
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <CalIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">
                          {lang === "fr" ? `Échéance ${i.index}/${i.total}` : `Installment ${i.index}/${i.total}`}
                        </span>
                        <span className="text-muted-foreground text-xs">•</span>
                        <span className="text-sm">{fmtDate(i.due_date, lang)}</span>
                      </div>
                      {(i.status === "failed" || i.status === "abandoned") && i.last_failure_message && (
                        <div className="text-xs text-red-700 mt-1 truncate" title={i.last_failure_message}>
                          {i.last_failure_message}
                        </div>
                      )}
                      {i.status === "paid" && i.paid_at && (
                        <div className="text-xs text-emerald-700 mt-0.5">
                          {lang === "fr" ? "Encaissé le " : "Collected on "}{fmtDate(i.paid_at, lang)}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-semibold mb-1">{(i.amount || 0).toFixed(2)} €</div>
                      <Badge className={`gap-1 text-[10px] ${meta.color}`}>
                        <Icon className={`w-3 h-3 ${i.status === "processing" ? "animate-spin" : ""}`} />
                        {lang === "fr" ? meta.fr : meta.en}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SummaryCard({ label, value, tone = "muted" }) {
  const tones = {
    muted: "border-muted bg-card",
    success: "border-emerald-200 bg-emerald-50",
    danger: "border-red-200 bg-red-50",
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1 flex items-center gap-2"><Banknote className="w-5 h-5 opacity-70" /> {value}</div>
    </div>
  );
}
